import { Stack, Typography } from "@mui/material";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowParams,
} from "@mui/x-data-grid-pro";
import { useMemo, type FC } from "react";
import type { ChannelName, ChannelStatus } from "../../../../channels/types";
import type { GetPersonsInSegmentJsonPerson } from "../types";

export const PreviewDatagrid: FC<{
  channelName: ChannelName;
  persons: GetPersonsInSegmentJsonPerson[];
  connection?: string;
}> = ({ channelName, persons, connection }) => {
  const columns: GridColDef<GetPersonsInSegmentJsonPerson>[] = useMemo(
    () => [
      {
        field: "first_name",
        headerName: "First Name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last Name",
        flex: 1,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
      },
      ...(channelName
        ? [
            {
              field: "channels",
              headerName: "Channel Display Name",
              flex: 1,
              valueFormatter: (value: Record<string, ChannelStatus>) => {
                if (!channelName) return "";
                return value[channelName]?.display || "";
              },
              renderCell: (
                params: GridRenderCellParams<
                  GetPersonsInSegmentJsonPerson,
                  GetPersonsInSegmentJsonPerson["channels"]
                >,
              ) => {
                if (!channelName) return "";
                return (params.value as Record<string, ChannelStatus>)[channelName]?.display || "";
              },
            },
          ]
        : []),
    ],
    [channelName],
  );

  const connectionColumns: GridColDef[] = useMemo(
    () => [{ field: "email", headerName: "Email", flex: 1 }],
    [],
  );

  const getDetailPanelContent = useMemo(
    () =>
      connection
        ? (params: GridRowParams<GetPersonsInSegmentJsonPerson>) => {
            const connections = (params.row.connections || []) as Connection[];

            return (
              <Stack direction="column" spacing={2} sx={{ p: 2, width: "100%" }}>
                <Typography variant="subtitle1">{connection} of</Typography>
                <DataGridPro
                  rows={connections}
                  columns={connectionColumns}
                  hideFooter
                  density="compact"
                />
              </Stack>
            );
          }
        : undefined,
    [connection, connectionColumns],
  );

  return (
    <DataGridPro
      rows={persons}
      columns={columns}
      getDetailPanelContent={connection ? getDetailPanelContent : undefined}
      getDetailPanelHeight={(params: GridRowParams<GetPersonsInSegmentJsonPerson>) =>
        connection && (params.row.connections as Connection[])?.length
          ? (params.row.connections as Connection[]).length * 52 + 120
          : 0
      }
      pagination
      showToolbar
    />
  );
};

type Connection = {
  id: string;
  email: string;
  employee_id: string | null;
};
